<script setup>
import { ref, computed, provide, watch } from 'vue'
import { useRoute } from 'vue-router'
import { marked } from 'marked'
import ImageViewer from '@/components/iiif/ImageViewer.vue'
import CoachmarkStory from '@/components/coachmark/CoachmarkStory.vue'
import Coachmark from '@/components/coachmark/Coachmark.vue'

import useInteraction from '@/composables/interaction'

import themeData from '@/data/index'

const route = useRoute()

const activeThemeData = computed(() => {
  switch (route.params.theme) {
    case 'pageElements':
      return themeData.pageElements
    case 'photograms':
      return themeData.photograms
    case 'portraits':
      return themeData.portraits
    case 'centralParis':
      return themeData.centralParis
    case 'britishPhotography':
      return themeData.britishPhotography
    case 'parisOutskirts':
      return themeData.parisOutskirts
    case 'gardens':
    default:
      return themeData.gardens
  }
})

const activePoi = ref(1)

watch(activePoi, (newPoi) => {
  useInteraction(newPoi !== 1 ? 'guided_poi_view' : null, {
    poi: newPoi,
    theme: route.params.theme
  })
})

watch(() => route.params.theme, () => {
  activePoi.value = 1
})

const zoomCoords = computed(() => {
  return activeThemeData.value.pois[activePoi.value - 1]
})

provide('zoomCoords', zoomCoords)
</script>

<template>
  <CoachmarkStory id="guided-tour" :marks="2" v-slot="{ activeIndex: activeCoachmarkIndex }">
    <div>
      <ImageViewer
        :canvas-index="activeThemeData.canvasIndex"
        :overlay-controls="false"
        class="h-full"
      />
    </div>
    <div class="poi-column flex flex-col justify-between">
      <h1 class="font-semibold text-4xl">{{ $t(`guided.${route.params.theme}.title`) }}</h1>
      <div class="flex flex-col gap-y-4">
        <h2 class="font-semibold text-2xl">{{ $t(`guided.${route.params.theme}.poi${activePoi}.title`) }}</h2>
        <div class="poi-content" v-html="marked.parse($t(`guided.${route.params.theme}.poi${activePoi}.body`))" />
        <Coachmark
          :message="$t('coachmarks.guided_tour_thumbnails')"
          :active="activeCoachmarkIndex === 1"
          alignment="right-top"
          class="border-cream border-t-2 grid items-center justify-items-center gap-y-2 py-2 poi-grid"
        >
          <img
            v-for="i in activeThemeData.pois.length"
            :key="i"
            :src="`/images/${route.params.theme}${i}.jpg`"
            class="w-[100px] h-[100px] box-content outline-purple outline-4 transition"
            :class="{ 'outline': i === activePoi }"
            @click="activePoi = i"
            draggable="false"
          >
        </Coachmark>
      </div>
    </div>
    <Coachmark
      :message="$t('coachmarks.guided_tour_tabs')"
      :active="activeCoachmarkIndex === 0"
      alignment="bottom-center"
      class="border-cream border-t-2 col-span-2 pb-[20px] px-[10px] grid grid-flow-col grid-cols-7 gap-[10px]"
    >
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'pageElements' } }" class="menu-tab">
        {{ $t('guided.pageElements.title') }}
      </RouterLink>
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'photograms' } }" class="menu-tab">
        {{ $t('guided.photograms.title') }}
      </RouterLink>
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'portraits' } }" class="menu-tab">
        {{ $t('guided.portraits.title') }}
      </RouterLink>
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'centralParis' } }" class="menu-tab">
        {{ $t('guided.centralParis.title') }}
      </RouterLink>
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'britishPhotography' } }" class="menu-tab">
        {{ $t('guided.britishPhotography.title') }}
      </RouterLink>
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'parisOutskirts' } }" class="menu-tab">
        {{ $t('guided.parisOutskirts.title') }}
      </RouterLink>
      <RouterLink :to="{ name: 'guidedTour', params: { theme: 'gardens' } }" class="menu-tab">
        {{ $t('guided.gardens.title') }}
      </RouterLink>
    </Coachmark>
  </CoachmarkStory>
</template>

<style lang="postcss">
#guided-tour {
  @apply w-full h-full grid;
  grid-template-columns: 1265px 1fr;
  grid-template-rows: 1fr 80px;
}

.menu-tab {
  @apply flex justify-center items-center bg-olive font-medium;
  box-shadow: 0 4px 4px rgb(0 0 0 / 20%);
}

.menu-tab.router-link-exact-active {
  @apply bg-purple;
}

.poi-column {
  padding: 100px 90px 20px 40px;
}

.poi-content img {
  @apply w-full object-contain object-left mt-4;
  height: 275px;
  pointer-events: none;
}

.poi-grid {
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
}
</style>
